/* custom-help.js
   Unified help-drawer handler for Custom Hydration (Flavors + Sliders)

   Markup requirements:
   - Button:  <button type="button" class="custom-help-btn" aria-controls="drawer-id" aria-haspopup="dialog" aria-expanded="false" ...>?</button>
   - Drawer:  <side-drawer id="drawer-id"> ... <button class="js-close-drawer">...</button> ... </side-drawer>

   Notes:
   - We rely ONLY on aria-controls (no data-drawer-target).
   - We move each <side-drawer> under <body> for proper overlay stacking.
   - If the theme exposes drawer.open(trigger)/drawer.close(), we call those; otherwise we toggle attributes.
   - We keep bindings idempotent using dataset flags.
*/

(function () {
  const BTN_SELECTOR = '.custom-help-btn';
  const WIRED_FLAG = 'helpWired';
  const DRAWER_WIRED_FLAG = 'drawerWired';

  function getDrawerIdFromBtn(btn) {
    const id = (btn.getAttribute('aria-controls') || '').trim();
    return id && id[0] === '#' ? id.slice(1) : id;
  }

  function resolveDrawer(root, id) {
    if (!id) return null;
    return document.getElementById(id) || root.querySelector('#' + id) || null;
  }

  function moveDrawerToBody(drawer) {
    if (drawer && drawer.parentElement !== document.body) {
      document.body.appendChild(drawer);
    }
  }

  function openDrawer(drawer, triggerBtn) {
    // Remove hidden state (for non-component fallback and a11y)
    drawer.removeAttribute('hidden');
    drawer.setAttribute('aria-hidden', 'false');

    // Theme component API (preferred)
    if (typeof drawer.open === 'function') {
      drawer.open(triggerBtn);
    } else {
      // Fallback: add @open and dispatch an event if anything's listening
      drawer.setAttribute('open', '');
      drawer.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
      // Optional: add a simple overlay class if your CSS uses it
      document.documentElement.classList.add('drawer-open');
    }
  }

  function closeDrawer(drawer) {
    if (typeof drawer.close === 'function') {
      drawer.close();
    } else {
      drawer.removeAttribute('open');
      drawer.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
      document.documentElement.classList.remove('drawer-open');
    }
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('hidden', '');
  }

  function wireDrawerCloseHandlers(drawer, relatedButtons) {
    if (drawer.dataset[DRAWER_WIRED_FLAG] === '1') return;

    // Close buttons inside the drawer
    drawer.addEventListener('click', (e) => {
      if (e.target.closest('.js-close-drawer')) {
        closeDrawer(drawer);
        // Set aria-expanded back to false on all buttons controlling this drawer
        relatedButtons.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
      }
    });

    // Escape key closes the drawer
    const onKeyDown = (e) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        // Only close if this drawer is currently visible
        const isHidden = drawer.getAttribute('aria-hidden') === 'true' || drawer.hasAttribute('hidden') === true;
        if (!isHidden) {
          closeDrawer(drawer);
          relatedButtons.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);

    // If the theme emits a close event, sync aria
    drawer.addEventListener('drawer:close', () => {
      relatedButtons.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
    });

    drawer.dataset[DRAWER_WIRED_FLAG] = '1';
  }

  function wireOneButton(root, btn) {
    if (btn.dataset[WIRED_FLAG] === '1') return;

    const drawerId = getDrawerIdFromBtn(btn);
    if (!drawerId) return;

    const drawer = resolveDrawer(root, drawerId);
    if (!drawer) return;

    moveDrawerToBody(drawer);

    // Track all buttons that point to this drawer (for aria-expanded sync)
    const siblingButtons = Array.from(document.querySelectorAll(`${BTN_SELECTOR}[aria-controls="${drawerId}"]`));

    // Open handler
    const onClick = (e) => {
      e.preventDefault();
      btn.setAttribute('aria-expanded', 'true');
      openDrawer(drawer, btn);
      // If multiple buttons control the same drawer, set the others to true too (optional)
      siblingButtons.forEach((b) => {
        if (b !== btn) b.setAttribute('aria-expanded', 'true');
      });
    };

    btn.addEventListener('click', onClick);

    // Ensure close behavior is wired once per drawer
    wireDrawerCloseHandlers(drawer, siblingButtons);

    btn.dataset[WIRED_FLAG] = '1';
  }

  function wireAll(root = document) {
    root.querySelectorAll(BTN_SELECTOR).forEach((btn) => wireOneButton(root, btn));
  }

  // Initial wire
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => wireAll(document));
  } else {
    wireAll(document);
  }

  // Re-wire when sections hot-reload in the Theme Editor
  document.addEventListener('shopify:section:load', (e) => {
    const sectionEl = document.getElementById(`shopify-section-${e.detail.sectionId}`) || document;
    wireAll(sectionEl);
  });
})();
